import React, { useEffect, useState } from 'react';
import { isEmpty, isEqual, keyBy, omitBy, pickBy, some } from 'lodash';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';

import CardLayout from '@magoosh/layouts/card';
import Filters from '@magoosh/organisms/filters';
import GuidedStudyNav from '@magoosh/organisms/intra_module_nav';
import { parseCustomDateRange } from '@magoosh/organisms/filters/custom_date_range';
import { FilterConfig } from '@magoosh/organisms/filters/types';
import useSearchQuery from '@magoosh/utils/use_search_query';
import { paths } from 'config/path_helpers';
import { AnswerData } from 'types/answer';

import ContextualLink from '@test_prep/app/components/contextual_link';
import { useAppContext } from '@test_prep/app/context';
import { useQueries } from '@test_prep/app/queries';

import AnswersTable from './components/answers_table';
import PracticeSessionHeader from './components/practice_session_header';
import ScoreReportStatus from './components/score_report_status';
import StatsSummary from './components/stats_summary';

const ReviewAnswers: React.FC<{}> = (props) => {
  const { branding } = useAppContext().data;
  const history = useHistory();
  const q = useQueries();
  const searchQuery = useSearchQuery();
  const [answers, setAnswers] = useState<AnswerData[]>([])

  const pagePath = paths.reviewAnswers(searchQuery);
  const page = q.pageQuery(pagePath, { keepPreviousData: true }).data;

  let filtersConfig = [
    {
      all: true,
      allowWhileFocused: true,
      default: 'all',
      key: 'section',
      multiple: true,
      multipleStyle: 'select',
      name: 'Section',
      options: (page?.sections || []).map((section) => (
        { group: section.group, name: section.name, value: section.id }
      ))
    },
    {
      all: true,
      allowWhileFocused: true,
      default: 'all',
      key: 'subject',
      multiple: true,
      multipleStyle: 'select',
      name: 'Subject',
      options: (page?.subjects || []).map((subject) => (
        { name: subject.name, value: subject.id }
      ))
    },
    {
      all: true,
      allowWhileFocused: true,
      default: 'all',
      key: 'result',
      multiple: true,
      multipleStyle: 'select',
      name: 'Result',
      options: [
        { name: 'Correct', value: 'correct' },
        { name: 'Incorrect', value: 'incorrect' }
      ]
    },
    {
      all: true,
      allowWhileFocused: true,
      default: 'all',
      key: 'difficulty',
      multiple: true,
      multipleStyle: 'select',
      name: 'Difficulty',
      options: [
        { name: 'Easy', value: 'easy' },
        { name: 'Medium', value: 'medium' },
        { name: 'Hard', value: 'hard' },
        { name: 'Very Hard', value: 'very_hard' }
      ]
    },
    {
      all: true,
      allowWhileFocused: true,
      default: 'all',
      key: 'other',
      multiple: true,
      multipleStyle: 'select',
      name: 'Other',
      options: [
        { name: 'Only Flagged', value: 'flag' },
        { name: 'Only With Note', value: 'note' },
        { name: 'Slow Pace', value: 'slow' },
        { name: 'Unwatched Video Explanation', value: 'unwatched' }
      ]
    },
    {
      default: 'all_time',
      key: 'period',
      name: 'Period',
      options: [
        { name: 'All Time', value: 'all_time' },
        { name: 'Custom', value: 'custom' },
        { name: 'Since Last Practice Test', value: 'since_last_practice_test' }
      ],
      right: true
    }
  ] as FilterConfig[];

  const answersWithScoreReports = () => {
    return answers.filter((answer) => !!answer.scoreReport)
  };

  const breadcrumbs = () => {
    if ((!page && searchQuery['study_item']) || page?.guidedStudyNav) {
      return;
    } else if (page?.practiceSession) {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Review', path: paths.reviewAnswers() },
        { name: page.practiceSession.name }
      ];
    } else {
      return [
        { name: 'Home', path: paths.dashboard() },
        { name: 'Review' }
      ];
    }
  };

  const defaultFilters = () => {
    let result = {};
    filtersConfig.map((filterConfig) => {
      result[filterConfig.key] = filterConfig.default;
    });
    return result;
  };

  const extractUrlFilters = () => {
    let result = {} as any;

    Object.keys(searchQuery).forEach((key) => {
      const value = searchQuery[key];
      if (isEmpty(value)) return;

      if (key.startsWith('filters')) {
        const match = key.match(/^filters\[(.+)\]$/);
        if (match) result[match[1]] = value;
      }
    });

    return result;
  };


  const handleFiltersChanged = (updatedFilters) => {
    history.replace(paths.reviewAnswers({ filters: omitBy(updatedFilters, (value) => !value) }));
  };

  const urlFilters = extractUrlFilters();
  const [filters, setFilters] = useState<{ [key: string]: string }>({ ...defaultFilters(), ...urlFilters });

  // Handle any invalid filter values
  useEffect(() => {
    if (!filters) return;

    // Ensure any custom period filter value is parseable
    if (filters.period?.startsWith('custom')) {
      try {
        const dateRange = parseCustomDateRange(filters.period);

        if (!dateRange.startDate || !dateRange.endDate) {
          setFilters({
            ...filters,
            period: 'all_time'
          });
        }
      } catch {
        setFilters({
          ...filters,
          period: 'all_time'
        });
      }
    } else if (filters.period !== 'all_time' && filters.period !== 'since_last_practice_test') {
      setFilters({
        ...filters,
        period: 'all_time'
      });
    }
  }, [filters]);

  // Only answers with score reports will be passed in here
  const handleScoreReportAnswersChanged = (updatedAnswerData: AnswerData[]) => {
    const updatedAnswersById = keyBy(updatedAnswerData, 'id');

    const updatedAnswers = answers.map((answer) => (
      updatedAnswersById[answer.id] || answer
    ));

    setAnswers(updatedAnswers);
  };

  const scopedToPracticeSession = () => {
    return (filters['practice_session_id'] || filters['practice_session_ids']) && page.practiceSession
  };

  useEffect(() => {
    if (isEmpty(urlFilters) && !isEqual(filters, defaultFilters())) {
      setFilters(defaultFilters());
    } else if (some(urlFilters, (value, key) => filters[key] !== value)) {
      setFilters({
        ...filters,
        ...urlFilters
      });
    }
  }, [urlFilters]);

  return (
    <>
      <Helmet>
        <title>Review - Magoosh {branding.examName}</title>
      </Helmet>

      <CardLayout breadcrumbs={breadcrumbs()} linkComponent={ContextualLink} loading={!page}>
        {({ loading }) => {
          if (loading) return;

          return (
            <div className="d-flex flex-column gap-m">
              {
                page.guidedStudyNav && (
                  <div className="u-margin-T-m">
                    <GuidedStudyNav {...page.guidedStudyNav} />
                  </div>
                )
              }

              <div className="d-flex flex-column gap-s gap-lg-m">
                {/* The GuidedStudyNav already provides a bottom border */}
                {!page.guidedStudyNav && <hr className="u-margin-A-n" />}

                <Filters
                  allowClear
                  filters={filters}
                  filtersConfig={filtersConfig}
                  focused={!!page.practiceSession}
                  insertClearAfter="other"
                  onChange={handleFiltersChanged}
                />

                <hr className="u-margin-A-n" />
              </div>

              {
                scopedToPracticeSession() && (
                  <CardLayout.Card borderGradient>
                    <PracticeSessionHeader {...page.practiceSession} />
                  </CardLayout.Card>
                )
              }

              <CardLayout.Card borderGradient>
                <StatsSummary filters={filters} practiceSession={page.practiceSession} />
              </CardLayout.Card>

              {
                page.practiceSession && answersWithScoreReports().length > 0 && (
                  <CardLayout.Card borderGradient>
                    <ScoreReportStatus
                      answers={answersWithScoreReports()}
                      onChange={handleScoreReportAnswersChanged}
                    />
                  </CardLayout.Card>
                )
              }

              <div className="d-flex flex-column gap-s gap-lg-m">
                <div className="align-items-end d-flex">
                  <h3 className="flex-1 u-margin-A-n u-margin-T-s">
                    <strong>Questions</strong>
                  </h3>
                </div>

                <hr className="u-margin-A-n" />
              </div>

              <CardLayout.Card style={{ padding: 0 }}>
                <AnswersTable answers={answers} filters={filters} onChange={setAnswers} />
              </CardLayout.Card>
            </div>
          );
        }}
      </CardLayout>
    </>
  );
};

export default ReviewAnswers;
